import _get from 'lodash/get';
import _chunk from 'lodash/chunk';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import client from 'utils/contentfulService';
import { filterBasedOnRequiredTags } from 'utils/analyzeClientResource';

const MAX_IDS = 100;

const getLinks = async (
  key,
  {
    clientId,
    clientGroupId,
    ctfLocaleFilter,
    profileTags,
    requiredTagsOptions,
    insuranceTags,
    insuranceOptions,
    show,
    limit = 1000,
  },
) => {
  const clientIds = [clientId];
  if (clientGroupId) clientIds.push(clientGroupId);

  const excludedTagsFilter = {};
  if (!_isEmpty(profileTags)) {
    excludedTagsFilter['fields.excludedTags.sys.id[nin]'] = profileTags
      .map(tag => tag.id)
      .join(',');
  }

  try {
    const ctfPayload = {
      content_type: 'link',
      'fields.client.sys.id[in]': clientIds.join(','),
      'fields.reviewStatus': 'Accepted',
      limit,
      ...excludedTagsFilter,
      ...ctfLocaleFilter,
    };

    if (show) {
      ctfPayload['fields.show[in]'] = show;
    }

    const response = await client.getEntries(ctfPayload);

    const readMoreIds = (response.items ?? [])
      .map(item => _get(item, 'fields.readMoreReference.sys.id'))
      .filter(Boolean);

    let readMoreMap = new Map();
    if (readMoreIds.length > 0) {
      const batches = _chunk(readMoreIds, MAX_IDS);
      const readMoreResponses = await Promise.all(
        batches.map(batch =>
          client.getEntries({
            select: 'sys.id,fields.availableLanguages',
            content_type: 'context',
            'sys.id[in]': batch.join(','),
            include: 1,
            ...ctfLocaleFilter,
          }),
        ),
      );

      readMoreMap = new Map(
        readMoreResponses
          .flatMap(r => r.items)
          .map(entry => [entry.sys.id, entry]),
      );
    }

    if (response.items) {
      response.items.forEach(item => {
        const refId = _get(item, 'fields.readMoreReference.sys.id');
        if (refId && readMoreMap.has(refId)) {
          const readMoreEntry = readMoreMap.get(refId);
          const { availableLanguages = [] } = readMoreEntry.fields || {};
          _set(
            item,
            'fields.readMoreReference.fields.availableLanguages',
            availableLanguages,
          );
        }
      });
    }

    const filteredItems = filterBasedOnRequiredTags({
      items: _get(response, 'items', []),
      insuranceTags,
      insuranceOptions,
      profileTags,
      requiredTagsOptions,
      isSearchPreview: false,
    });

    const fieldsMapping = {
      shortDescription: 'fields.subtitle',
      sys: 'sys',
      name: 'fields.title',
      visibility: 'fields.assessmentResultsVisibility.user-resources',
    };

    const getLinkItemUnmappedFields = item => {
      const { subtitle, title, assessmentResultsVisibility, ...rest } =
        item?.fields || {};

      return rest;
    };

    const prettyData = filteredItems.map(item =>
      Object.keys(fieldsMapping).reduce((res, field) => {
        res[field] = _get(item, fieldsMapping[field]);
        return res;
      }, getLinkItemUnmappedFields(item)),
    );

    return prettyData;
  } catch (error) {
    throw new Error(error);
  }
};

export default getLinks;
